/**
 * Setup - the scenario builder.
 *
 * The dam list holds every site and every HEC-RAS plan parsed from the
 * modelling team's files; choosing one fills each field from that file. The
 * hazard > structure > mechanism picker narrows the same selection, and only
 * leaves the API allows - and that can produce a result - are rendered.
 *
 * Only fields the run reads are shown, in the three groups the run consumes
 * them in, beside a cross-section of the breach drawn to scale from the
 * current values. A site with no such leaf shows its parsed levels read-only.
 */

import { useDeferredValue, useEffect, useMemo, useState } from 'react'
import { useMutation, useQueries, useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'

import { api } from '../api/client'
import type { BreachPreviewRequest, Scenario } from '../api/types'
import { DemSelector } from './DemSelector'
import { useAppStore } from '../store/useAppStore'
import { DebrisQuantities } from './DebrisQuantities'
import { ModelParametersUsed, type CaseRecord } from './ModelParametersUsed'

type Value = number | string | null

interface Param {
  key: string
  label: string
  unit: string
  default: Value
  source: string
  drives: string
  required: boolean
  options: string[] | null
}
interface Leaf {
  allowed: boolean
  parameters?: Param[]
  runnability?: { runnable: boolean; alternative?: string | null }
}
interface Tree {
  site_id: string
  hazards: {
    hazard: string
    applies: boolean
    structures: { structure: string; mechanisms: { mechanism: string; allowed: boolean }[] }[]
  }[]
}
interface Preset {
  key: string
  site_id: string
  structure_type: string
  mechanism: string
  available: boolean
  program_version?: string
  values?: Record<string, { value: unknown; source: string }>
}
interface Sourced {
  value: unknown
  unit?: string
  source?: string
}
interface SiteDetail {
  levels?: Record<string, Sourced>
  reservoir_volume?: Sourced
}
export interface Site {
  id: string
  display_name: string
  hazard_class: string
  state?: string
  center?: { lat: number; lon: number }
  reservoir_volume?: { value: number | null; source?: string }
  runnable?: boolean
}
export interface SitesPayload {
  default: string
  run_site?: string
  sites: Site[]
}
export interface DamRef {
  id: number
  frl_m: number
  reservoir_bed_level_m: number
  dam_crest_level_m?: number | null
}

interface Choice {
  siteId: string
  structure: string | null
  mechanism: string | null
}
interface Usable {
  siteId: string
  hazard: string
  structure: string
  mechanism: string
  leaf: Leaf
}
interface Case extends Choice {
  key: string
  label: string
  preset: Preset | null
}
interface Range {
  min?: number
  minOpen?: boolean
  max?: number
  text: string
}

const LABEL = 'section-label'
const EASE = 'transition-colors duration-150 ease-[cubic-bezier(0.2,0,0,1)]'

const HAZARD: Record<string, string> = { DAM_FAILURE: 'Dam failure', FLASH_FLOOD: 'Flash flood' }
const STRUCTURE: Record<string, string> = {
  MASONRY: 'Masonry',
  EARTHEN: 'Earthen',
  JUNCTION: 'Junction',
  GLOF: 'Glacial lake outburst',
  CLOUDBURST: 'Cloudburst',
}
const MECHANISM: Record<string, string> = {
  OVERTOPPING: 'Overtopping',
  PIPING: 'Piping',
  MORAINE_BREACH: 'Moraine breach',
  RAINFALL_RUNOFF: 'Rainfall-runoff',
}
const ORDER = [...Object.keys(HAZARD), ...Object.keys(STRUCTURE), ...Object.keys(MECHANISM)]
const rank = (k: string) => {
  const i = ORDER.indexOf(k)
  return i < 0 ? ORDER.length : i
}
const SITE_SHORT: Record<string, string> = { UJJANI: 'Ujjani Dam', SIKKIM_GLOF: 'Sikkim', PIMPALGAON: 'Pimpalgaon Dam' }
const title = (map: Record<string, string>, k: string) =>
  map[k] ?? k.charAt(0) + k.slice(1).toLowerCase().replace(/_/g, ' ')

const FIELD_LABEL: Record<string, string> = {
  reservoir_level_m: 'Reservoir level at failure',
  lake_level_m: 'Lake level',
  trigger_m: 'Breach trigger level',
  moraine_crest_m: 'Moraine crest',
  breach_depth_m: 'Breach depth',
  breach_invert_m: 'Breach invert',
  breach_width_m: 'Breach width',
  breach_formation_time_s: 'Formation time',
  formation_time_h: 'Formation time',
  side_slope_z: 'Side slope',
  weir_coefficient: 'Weir coefficient Cw',
  grid_resolution_m: 'Grid resolution',
  duration_hours: 'Simulated duration',
}
const LEVEL_LABEL: Record<string, string> = {
  tbl_m: 'Top bank level',
  hfl_m: 'High flood level',
  frl_m: 'Full reservoir level',
  mddl_m: 'MDDL',
  outlet_sill_m: 'Outlet sill level',
  river_bed_m: 'River bed level',
}
const UNIT: Record<string, string> = { SI: 'm½/s' }

const GROUPS: { title: string; keys: string[] }[] = [
  { title: 'Reservoir', keys: ['reservoir_level_m', 'lake_level_m', 'trigger_m', 'moraine_crest_m'] },
  { title: 'Breach', keys: [] },
  { title: 'Computation', keys: ['grid_resolution_m', 'duration_hours'] },
]
const groupOf = (key: string) => GROUPS.find((g) => g.keys.includes(key))?.title ?? 'Breach'

/** The scenario columns the 2-D run reads; any other driving value goes to mechanism_params. */
const SCENARIO_COLUMNS = new Set([
  'breach_depth_m',
  'breach_width_m',
  'breach_formation_time_s',
  'reservoir_level_m',
  'grid_resolution_m',
  'duration_hours',
])

/** A file name (with optional line) inside a source string: `Trial (1).p01:152`, `study_area.yaml`. */
const FILE_RE = /([A-Za-z0-9_][A-Za-z0-9_.-]*(?: \(\d+\))?\.(?:[pgu]\d\d|yaml|py|csv|xlsx))(?::(\d+))?/
/** A spreadsheet cell reference: `'Match Imp Lvls'!C5`. */
const CELL_RE = /'([^']+)'!([A-Z]+\d+)/
function shortSource(source: string): string | null {
  const m = FILE_RE.exec(source)
  if (m) return m[2] ? `${m[1]}:${m[2]}` : m[1]
  const c = CELL_RE.exec(source)
  return c ? `'${c[1]}'!${c[2]}` : null
}

function presetValue(preset: Preset | null, key: string): { value: number; source: string } | null {
  const v = preset?.values
  if (!v) return null
  const hit = v[key]
  if (hit && typeof hit.value === 'number') return { value: hit.value, source: hit.source }
  const hours = v.formation_time_h
  if (key === 'breach_formation_time_s' && hours && typeof hours.value === 'number') {
    return { value: hours.value * 3600, source: hours.source }
  }
  return null
}

const f2 = (n: number) => n.toFixed(2)

function rangeFor(p: Param, mechanism: string, bed: number | null, crest: number | null): Range | null {
  const froehlich = p.source.includes('Froehlich') ? ' · blank: Froehlich (2008)' : ''
  switch (p.key) {
    case 'breach_depth_m':
    case 'breach_width_m':
    case 'breach_formation_time_s':
    case 'formation_time_h':
    case 'weir_coefficient':
      return { min: 0, minOpen: true, text: `> 0${froehlich}` }
    case 'side_slope_z':
      return { min: 0, text: '≥ 0' }
    case 'duration_hours':
      return { min: 0, minOpen: true, max: 240, text: '> 0 to 240' }
    case 'grid_resolution_m':
      return { min: 30, max: 2000, text: '30 to 2000' }
    case 'reservoir_level_m':
      if (bed === null) return null
      if (mechanism === 'PIPING' && crest !== null) {
        return { min: bed, minOpen: true, max: crest, text: `${f2(bed)} to ${f2(crest)} (bed to crest)` }
      }
      return { min: bed, minOpen: true, text: `> ${f2(bed)} (bed)` }
    case 'breach_invert_m':
      return crest === null ? null : { max: crest, text: `≤ ${f2(crest)} (crest)` }
  }
  return null
}

function isInvalid(v: Value, p: Param, r: Range | null): boolean {
  if (v === null) return p.required
  if (typeof v !== 'number') return false
  if (Number.isNaN(v)) return true
  if (!r) return false
  if (r.min !== undefined && (r.minOpen ? v <= r.min : v < r.min)) return true
  return r.max !== undefined && v > r.max
}

function caseLabel(siteId: string, structure: string, mechanism: string, sites: Site[]) {
  const short = SITE_SHORT[siteId] ?? sites.find((s) => s.id === siteId)?.display_name ?? siteId
  if (structure === 'GLOF') return `${short} — glacial lake outburst`
  return `${short} — ${title(STRUCTURE, structure).toLowerCase()}, ${title(MECHANISM, mechanism).toLowerCase()}`
}

/** A site's parsed levels and storage, each with its cell reference. */
function siteRows(d: SiteDetail | undefined) {
  if (!d) return []
  const rows: { key: string; label: string; value: string; unit: string; source: string | null }[] = []
  for (const [k, v] of Object.entries(d.levels ?? {})) {
    if (typeof v.value !== 'number' || k.includes('dem_datum')) continue
    rows.push({
      key: k,
      label: LEVEL_LABEL[k] ?? title({}, k.replace(/_m$/, '').toUpperCase()),
      value: v.value.toFixed(2),
      unit: v.unit || 'm',
      source: shortSource(v.source ?? ''),
    })
  }
  const vol = d.reservoir_volume
  if (vol && typeof vol.value === 'number') {
    rows.push({ key: 'storage', label: 'Storage at FRL', value: (vol.value / 1e6).toFixed(2), unit: 'Mm³', source: shortSource(vol.source ?? '') })
  }
  return rows
}

function Card({
  selected,
  onClick,
  label,
  sub,
}: {
  selected: boolean
  onClick: () => void
  label: string
  sub?: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={`min-w-[10rem] flex-1 rounded-[var(--r-control)] border bg-[var(--ink-surface-raised)] px-4 py-3.5 text-left ${EASE} ${
        selected
          ? 'border-[var(--ink-accent)] text-[var(--ink-text)]'
          : 'border-[var(--ink-border)] text-[var(--ink-text-dim)] hover:border-[var(--ink-border-strong)]'
      }`}
    >
      <span className="block text-14 font-medium">{label}</span>
      {sub && <span className="mt-0.5 block text-14 text-[var(--ink-text-dim)]">{sub}</span>}
    </button>
  )
}

function Step({ n, name, children }: { n: number; name: string; children: React.ReactNode }) {
  return (
    <section>
      <div className={`mb-2 ${LABEL}`}>
        <span className="font-mono text-[var(--ink-text-faint)]">{n}</span>&nbsp;&nbsp;{name}
      </div>
      <div className="flex flex-wrap gap-2">{children}</div>
    </section>
  )
}

function Panel({ name, children }: { name: string; children: React.ReactNode }) {
  return (
    <section className="panel" data-panel={name}>
      <header className="panel-header">
        <h2>{name}</h2>
      </header>
      <div className="space-y-3 p-4">{children}</div>
    </section>
  )
}

function Suffixed({ unit, bad, children }: { unit: string; bad?: boolean; children: React.ReactNode }) {
  return (
    <div
      className={`mt-1 flex h-10 rounded-[var(--r-control)] border bg-[var(--ink-bg)] ${EASE} ${
        bad ? 'border-[var(--ink-alert)]' : 'border-[var(--ink-border)] focus-within:border-[var(--ink-accent)]'
      }`}
    >
      {children}
      <span className="flex w-16 shrink-0 items-center justify-center border-l border-[var(--ink-border)] font-mono text-14 text-[var(--ink-text-dim)]">
        {unit}
      </span>
    </div>
  )
}


export function ScenarioBuilder({
  dam,
  onSiteChange,
  onSaved,
}: {
  dam: DamRef | null
  onSiteChange: (siteId: string) => void
  onSaved: (scenario: Scenario) => void
}) {
  const navigate = useNavigate()
  const activeSiteId = useAppStore((s) => s.activeSiteId)
  const sites = useQuery<SitesPayload>({ queryKey: ['sites'], queryFn: () => api.raw('/sites') })
  const presets = useQuery<Preset[]>({ queryKey: ['scenario-presets'], queryFn: () => api.raw('/scenarios/presets') })
  const registry = useQuery<CaseRecord[]>({
    queryKey: ['hecras-cases'],
    queryFn: () => api.raw('/hecras/cases'),
    staleTime: Infinity,
  })
  const siteList = useMemo(() => sites.data?.sites ?? [], [sites.data])
  const runSite = sites.data?.run_site ?? sites.data?.default ?? null

  const trees = useQueries({
    queries: siteList.map((s) => ({
      queryKey: ['hierarchy', s.id],
      queryFn: () => api.raw<Tree>(`/scenarios/hierarchy?site_id=${encodeURIComponent(s.id)}`),
      staleTime: Infinity,
    })),
  })
  const candidates = trees.flatMap((t, i) =>
    !t.data
      ? []
      : t.data.hazards
          .filter((h) => h.applies)
          .flatMap((h) =>
            h.structures.flatMap((s) =>
              s.mechanisms
                .filter((m) => m.allowed)
                .map((m) => ({ siteId: siteList[i].id, hazard: h.hazard, structure: s.structure, mechanism: m.mechanism })),
            ),
          ),
  )
  const leaves = useQueries({
    queries: candidates.map((c) => ({
      queryKey: ['hierarchy-leaf', c.siteId, c.structure, c.mechanism],
      queryFn: () =>
        api.raw<Leaf>(
          `/scenarios/hierarchy/leaf?site_id=${encodeURIComponent(c.siteId)}&structure=${c.structure}&mechanism=${c.mechanism}`,
        ),
      staleTime: Infinity,
    })),
  })
  const ready = siteList.length > 0 && trees.every((t) => !t.isPending) && leaves.every((l) => !l.isPending) && !presets.isPending && !registry.isPending
  // A leaf renders only if it produces a result: a 2-D run, or a run elsewhere it hands over to.
  const usable: Usable[] = candidates.flatMap((c, i) => {
    const leaf = leaves[i]?.data
    return leaf?.allowed && (leaf.runnability?.runnable || leaf.runnability?.alternative) ? [{ ...c, leaf }] : []
  })

  // The case list is the HEC-RAS case registry: only cases the team supplied a plan file for.
  const cases: Case[] = useMemo(
    () =>
      (registry.data ?? [])
        .filter((r) => siteList.some((s) => s.id === r.site_id))
        .map((r) => ({
          key: r.case_id,
          label: r.label,
          siteId: r.site_id,
          structure: r.structure,
          mechanism: r.mechanism,
          preset:
            (presets.data ?? []).find(
              (p) => p.available && p.site_id === r.site_id && p.structure_type === r.structure && p.mechanism === r.mechanism,
            ) ?? null,
        })),
    [registry.data, presets.data, siteList],
  )

  const [choice, setChoice] = useState<Choice | null>(null)
  const [caseKey, setCaseKey] = useState<string | null>(null)
  const want = choice ?? cases.find((c) => c.siteId === activeSiteId) ?? cases[0] ?? null
  const current: Usable | null =
    !ready || !want
      ? null
      : usable.find((u) => u.siteId === want.siteId && u.structure === want.structure && u.mechanism === want.mechanism) ??
        usable.find((u) => u.siteId === want.siteId && u.structure === want.structure) ??
        usable.find((u) => u.siteId === want.siteId) ??
        null

  const matchesCurrent = (c: Case) =>
    current !== null && c.siteId === current.siteId && c.structure === current.structure && c.mechanism === current.mechanism
  const selectedCase = cases.find((c) => c.key === caseKey && matchesCurrent(c)) ?? cases.find(matchesCurrent) ?? null
  const record = (registry.data ?? []).find((r) => r.case_id === selectedCase?.key) ?? null

  // A site with no leaf that produces a result: its parsed levels, read-only.
  const siteOnly = ready && want && !current ? siteList.find((s) => s.id === want.siteId) ?? null : null
  const detail = useQuery<SiteDetail>({
    queryKey: ['site-detail', siteOnly?.id],
    queryFn: () => api.raw(`/sites/${encodeURIComponent(siteOnly?.id ?? '')}`),
    enabled: siteOnly !== null,
  })

  const preset =
    (current &&
      (presets.data ?? []).find(
        (p) =>
          p.available && p.site_id === current.siteId && p.structure_type === current.structure && p.mechanism === current.mechanism,
      )) ||
    null
  const fields = useMemo(
    () => (current?.leaf.parameters ?? []).filter((p) => p.drives.startsWith('drives')),
    [current?.leaf],
  )
  const selKey = current ? `${current.siteId}|${current.structure}|${current.mechanism}` : ''

  const [values, setValues] = useState<Record<string, Value>>({})
  const [drafts, setDrafts] = useState<Record<string, string>>({})
  const [sources, setSources] = useState<Record<string, string | null>>({})
  const [demPath, setDemPath] = useState<string | null>(null)

  useEffect(() => {
    if (!current) return
    const v: Record<string, Value> = {}
    const s: Record<string, string | null> = {}
    for (const p of fields) {
      const parsed = record?.form[p.key]
      const hit = parsed ? { value: parsed.value, source: parsed.source } : presetValue(preset, p.key)
      v[p.key] = hit ? hit.value : p.default
      s[p.key] = shortSource(hit ? hit.source : p.source)
    }
    setValues(v)
    setSources(s)
    setDrafts({})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selKey, preset?.key, record?.case_id])

  /** "Copy to inputs": the registry record into the editable fields - on the user's click only. */
  const copyToInputs = (form: CaseRecord['form']) => {
    const keys = fields.map((p) => p.key).filter((k) => form[k] !== undefined)
    setValues((v) => ({ ...v, ...Object.fromEntries(keys.map((k) => [k, form[k].value])) }))
    setSources((s) => ({ ...s, ...Object.fromEntries(keys.map((k) => [k, shortSource(form[k].source)])) }))
    setDrafts((d) => Object.fromEntries(Object.entries(d).filter(([k]) => !keys.includes(k))))
  }

  const siteId = current?.siteId ?? siteOnly?.id ?? null
  useEffect(() => {
    if (siteId) onSiteChange(siteId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId])

  const num = (k: string) => {
    const v = values[k]
    return typeof v === 'number' && !Number.isNaN(v) ? v : null
  }
  const isRunSite = current !== null && current.siteId === runSite
  const mechanism = current?.mechanism ?? ''

  // Froehlich (2008) width and time, from the API, for a blank width or time on the 2-D run.
  const depth = num('breach_depth_m')
  const level = num('reservoir_level_m')
  const width = num('breach_width_m')
  const time = num('breach_formation_time_s')
  const previewReq: BreachPreviewRequest | null =
    isRunSite && dam && depth !== null && depth > 0 && level !== null && (width === null || time === null)
      ? {
          dam_id: dam.id,
          reservoir_level_m: level,
          breach_depth_m: depth,
          failure_mode: mechanism === 'PIPING' ? 'piping' : 'overtopping',
          breach_width_m: width,
          breach_formation_time_s: time,
          reservoir_inflow_cumecs: 0,
          duration_hours: num('duration_hours') ?? 24,
        }
      : null
  const previewKey = useDeferredValue(previewReq ? JSON.stringify(previewReq) : null)
  const preview = useQuery({
    queryKey: ['breach-preview', previewKey],
    queryFn: () => api.previewBreach(JSON.parse(previewKey as string) as BreachPreviewRequest),
    enabled: previewKey !== null,
    staleTime: Infinity,
    retry: false,
  })
  const froehlich = previewReq && previewKey && preview.data ? preview.data : null

  // Crest, for the reservoir panel and the field ranges: the moraine field, else the plan's connection weir, else the dam data sheet.
  const presetCrest = (() => {
    const inv = preset?.values?.breach_invert_m?.value
    const d = preset?.values?.breach_depth_m?.value
    return typeof inv === 'number' && typeof d === 'number' ? inv + d : null
  })()
  const crest = num('moraine_crest_m') ?? presetCrest ?? (isRunSite ? dam?.dam_crest_level_m ?? null : null)
  const bed = isRunSite && dam ? dam.reservoir_bed_level_m : null

  const ranges = Object.fromEntries(fields.map((p) => [p.key, rangeFor(p, mechanism, bed, crest)]))
  const anyInvalid = fields.some((p) => isInvalid(values[p.key] ?? null, p, ranges[p.key]))

  const chainageDefault = current?.leaf.parameters?.find((p) => p.key === 'breach_chainage_m')?.default
  const body = () => {
    const extra: Record<string, Value> = {}
    for (const p of fields) if (!SCENARIO_COLUMNS.has(p.key)) extra[p.key] = values[p.key] ?? null
    return {
      name: current ? caseLabel(current.siteId, current.structure, current.mechanism, siteList) : '',
      dam_id: dam?.id ?? 0,
      breach_depth_m: depth ?? 0,
      breach_width_m: width,
      breach_formation_time_s: time,
      reservoir_level_m: level ?? 0,
      failure_mode: mechanism === 'PIPING' ? ('piping' as const) : ('overtopping' as const),
      reservoir_inflow_cumecs: 0,
      baseflow_cumecs: 0,
      manning_n_channel: null,
      manning_n_floodplain: null,
      duration_hours: num('duration_hours') ?? 24,
      grid_resolution_m: num('grid_resolution_m') ?? 360,
      output_interval_s: 900,
      dem_path: demPath,
      hydrograph_csv_path: null,
      notes: null,
      site_id: current?.siteId ?? null,
      hazard_type: current?.hazard ?? null,
      structure_type: current?.structure ?? null,
      mechanism: current?.mechanism ?? null,
      // A parsed plan's station is on its own datum; only the leaf's own chainage is ours.
      breach_chainage_m: !preset && typeof chainageDefault === 'number' ? chainageDefault : null,
      mechanism_params: extra,
    }
  }

  const run = useMutation({
    mutationFn: async () => {
      const scenario = await api.createScenario(body())
      await api.createRun(scenario.id)
      return scenario
    },
    onSuccess: (scenario) => onSaved(scenario),
  })

  const pick = (c: Choice) => setChoice(c)

  const caseSelect = (value: string, list: Case[]) => (
    <label className="block">
      <span className={`block ${LABEL}`}>Dam and failure case</span>
      <select
        value={value}
        onChange={(e) => {
          const c = list.find((o) => o.key === e.target.value)
          if (!c) return
          setCaseKey(c.key)
          pick({ siteId: c.siteId, structure: c.structure, mechanism: c.mechanism })
        }}
        className="mt-1 h-11 w-full rounded-[var(--r-control)] border border-[var(--ink-border)] bg-[var(--ink-surface-raised)] px-3 text-14 font-medium text-[var(--ink-text)] focus:border-[var(--ink-accent)] focus:outline-none"
        data-testid="case-select"
      >
        {!list.some((o) => o.key === value) && <option value="" hidden />}
        {list.map((c) => (
          <option key={c.key} value={c.key}>
            {c.label}
          </option>
        ))}
      </select>
    </label>
  )

  const sourceRow = (text: string) =>
    text && (
      <div className="mt-2 flex items-baseline gap-3" data-testid="case-source">
        <span className={LABEL}>Source</span>
        <span className="font-mono text-14 text-[var(--ink-text)]">{text}</span>
      </div>
    )

  const hazardStep = (selected: string) => (
    <Step n={1} name="Hazard">
      {[...new Set([...usable.map((u) => u.hazard), ...(siteOnly ? [siteOnly.hazard_class] : [])])]
        .sort((a, b) => rank(a) - rank(b))
        .map((h) => (
          <Card
            key={h}
            label={title(HAZARD, h)}
            sub={siteList
              .filter((s) => s.hazard_class === h && usable.some((u) => u.siteId === s.id))
              .map((s) => s.display_name)
              .join(', ')}
            selected={selected === h}
            onClick={() => {
              if (selected === h) return
              const u = usable.find((x) => x.hazard === h)
              if (u) pick({ siteId: u.siteId, structure: u.structure, mechanism: u.mechanism })
            }}
          />
        ))}
    </Step>
  )

  if (!current) {
    if (siteOnly) {
      const rows = siteRows(detail.data)
      const sheets = [...new Set(rows.map((r) => (r.source ? CELL_RE.exec(r.source)?.[1] : null)).filter((s): s is string => !!s))]
      return (
        <div className="space-y-5" data-testid="scenario-builder">
          <section>
            {caseSelect('', cases)}
            {sourceRow(sheets.map((s) => `'${s}'`).join(' · '))}
          </section>
          {hazardStep(siteOnly.hazard_class)}
          {detail.isPending ? (
            <div className="skeleton h-72" />
          ) : (
            rows.length > 0 && (
              <Panel name="Reservoir">
                <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                  {rows.map((r) => (
                    <div key={r.key} data-param={r.key}>
                      <span className={`block ${LABEL}`}>{r.label}</span>
                      <Suffixed unit={r.unit}>
                        <span className="flex min-w-0 flex-1 items-center justify-end px-2.5 font-mono text-14 tabular-nums text-[var(--ink-text)]">
                          {r.value}
                        </span>
                      </Suffixed>
                      {r.source && (
                        <span className="mt-1 block truncate text-right font-mono text-12 text-[var(--ink-text-faint)]">{r.source}</span>
                      )}
                    </div>
                  ))}
                </div>
              </Panel>
            )
          )}
        </div>
      )
    }
    return (
      <div className="space-y-4" data-testid="scenario-builder">
        <div className="skeleton h-11" />
        <div className="skeleton h-16" />
        <div className="skeleton h-16" />
        <div className="skeleton h-72" />
      </div>
    )
  }

  const sourceLine = (() => {
    const raw = preset ? Object.values(preset.values ?? {}).map((v) => v.source) : fields.map((p) => p.source)
    const files = new Map<string, Set<number>>()
    for (const s of raw) {
      const m = FILE_RE.exec(s)
      if (!m) continue
      const lines = files.get(m[1]) ?? new Set<number>()
      if (m[2]) lines.add(Number(m[2]))
      files.set(m[1], lines)
    }
    const parts = [...files].map(([f, lines]) =>
      lines.size ? `${f} lines ${[...lines].sort((a, b) => a - b).join(', ')}` : f,
    )
    if (preset?.program_version) parts.push(`HEC-RAS ${preset.program_version}`)
    return parts.join(' · ')
  })()

  const structures = [...new Set(usable.filter((u) => u.siteId === current.siteId).map((u) => u.structure))]
  const mechanisms = usable.filter((u) => u.siteId === current.siteId && u.structure === current.structure)

  const setValue = (p: Param, raw: string) => {
    setDrafts((d) => ({ ...d, [p.key]: raw }))
    setValues((v) => ({ ...v, [p.key]: p.options ? raw || null : raw.trim() === '' ? null : Number(raw) }))
  }

  const placeholderFor = (key: string) => {
    if (!froehlich) return ''
    if (key === 'breach_width_m' && froehlich.width_source === 'froehlich') return froehlich.width_m.toFixed(1)
    if (key === 'breach_formation_time_s' && froehlich.formation_time_source === 'froehlich') {
      return froehlich.formation_time_s.toFixed(0)
    }
    return ''
  }

  const renderField = (p: Param) => {
    const v = values[p.key] ?? null
    const shown = drafts[p.key] ?? (v === null ? '' : String(v))
    const range = ranges[p.key]
    const bad = isInvalid(v, p, range)
    return (
      <label key={p.key} className="block" data-param={p.key}>
        <span className={`block ${LABEL}`}>{FIELD_LABEL[p.key] ?? p.label}</span>
        <Suffixed unit={UNIT[p.unit] ?? (p.unit || '—')} bad={bad}>
          {p.options ? (
            <select
              value={shown}
              onChange={(e) => setValue(p, e.target.value)}
              className="min-w-0 flex-1 bg-transparent px-2 font-mono text-14 text-[var(--ink-text)] outline-none"
            >
              {p.options.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          ) : (
            <input
              value={shown}
              inputMode="decimal"
              spellCheck={false}
              aria-invalid={bad}
              placeholder={placeholderFor(p.key)}
              onChange={(e) => setValue(p, e.target.value)}
              className="min-w-0 flex-1 bg-transparent px-2.5 text-right font-mono text-14 tabular-nums text-[var(--ink-text)] outline-none placeholder:text-[var(--ink-text-faint)]"
            />
          )}
        </Suffixed>
        {(range || sources[p.key]) && (
          <span className="mt-1 flex justify-between gap-3 text-12 leading-4 text-[var(--ink-text-dim)]">
            <span>{range?.text}</span>
            {sources[p.key] && <span className="truncate font-mono text-[var(--ink-text-faint)]">{sources[p.key]}</span>}
          </span>
        )}
      </label>
    )
  }

  const groups = GROUPS.map((g) => ({ ...g, fields: fields.filter((p) => groupOf(p.key) === g.title) }))
  const leafRuns = Boolean(current.leaf.runnability?.runnable)
  const runnable = leafRuns && isRunSite && dam !== null && siteList.find((s) => s.id === current.siteId)?.runnable !== false
  const alternative = !leafRuns ? current.leaf.runnability?.alternative ?? null : null

  return (
    <div className="space-y-5" data-testid="scenario-builder">
      <section>
        {caseSelect(selectedCase?.key ?? '', cases)}
        {sourceRow(sourceLine)}
      </section>

      {hazardStep(current.hazard)}
      <Step n={2} name="Structure">
        {structures.map((s) => (
          <Card
            key={s}
            label={title(STRUCTURE, s)}
            selected={current.structure === s}
            onClick={() => {
              const u = usable.find((x) => x.siteId === current.siteId && x.structure === s)
              if (u) pick({ siteId: u.siteId, structure: u.structure, mechanism: u.mechanism })
            }}
          />
        ))}
      </Step>
      <Step n={3} name="Mechanism">
        {mechanisms.map((u) => (
          <Card
            key={u.mechanism}
            label={title(MECHANISM, u.mechanism)}
            selected={current.mechanism === u.mechanism}
            onClick={() => pick({ siteId: u.siteId, structure: u.structure, mechanism: u.mechanism })}
          />
        ))}
      </Step>

      <div data-testid="leaf-parameters">
        <div className="space-y-4">
          {groups.map(
            (g) =>
              g.fields.length > 0 && (
                <Panel key={g.title} name={g.title}>
                  {g.fields.map(renderField)}
                  {g.title === 'Reservoir' && isRunSite && dam && (
                    <dl className="grid grid-cols-3 gap-2 border-t border-[var(--ink-border)] pt-3">
                      {(
                        [
                          ['Crest', crest],
                          ['FRL', dam.frl_m],
                          ['Bed', dam.reservoir_bed_level_m],
                        ] as [string, number | null][]
                      ).map(
                        ([k, n]) =>
                          n !== null && (
                            <div key={k}>
                              <dt className={LABEL}>{k}</dt>
                              <dd className="font-mono text-14 tabular-nums text-[var(--ink-text)]">{f2(n)}</dd>
                            </div>
                          ),
                      )}
                    </dl>
                  )}
                </Panel>
              ),
          )}
          {leafRuns && <DemSelector siteId={current.siteId} value={demPath} onChange={setDemPath} />}
        </div>
      </div>

      <ModelParametersUsed record={record} onCopy={copyToInputs} />

      <DebrisQuantities siteId={current.siteId} />

      {alternative ? (
        <button
          type="button"
          onClick={() => navigate(alternative)}
          className={`h-11 w-full rounded-[var(--r-control)] bg-[var(--ink-accent)] px-3 text-14 font-semibold text-[var(--ink-bg)] ${EASE} hover:bg-[var(--ink-accent-dim)]`}
        >
          Open level-pool reproduction
        </button>
      ) : (
        runnable && (
          <button
            type="button"
            disabled={anyInvalid || run.isPending}
            onClick={() => run.mutate()}
            className={`h-11 w-full rounded-[var(--r-control)] bg-[var(--ink-accent)] px-3 text-14 font-semibold text-[var(--ink-bg)] ${EASE} hover:bg-[var(--ink-accent-dim)] disabled:cursor-not-allowed disabled:opacity-40`}
            data-testid="run-button"
          >
            {run.isPending ? 'Starting run' : 'Run'}
          </button>
        )
      )}
      {run.isError && <p className="text-14 text-[var(--ink-alert-text)]">{(run.error as Error).message}</p>}
    </div>
  )
}
